import { motion } from 'framer-motion';
import { Check, MapPin, PenLine, FileText } from 'lucide-react';
import { fadeInUp, stagger } from './data';

const PORTAL_FEATURES = [
  'Shift-wise bookkeeping with bulk data entry for all machines',
  'Employee management with shift assignments and restricted access',
  'Service call submissions with priority levels and status tracking',
  'Real-time reports with credits, over/short calculations, and CSV export',
  'Custom portal themes and separate login credentials',
];

const MACHINE_ROWS = [
  { name: 'Fire Link #1', in_val: '$1,240', out: '$480', net: '$760' },
  { name: 'Buffalo Gold #3', in_val: '$980', out: '$350', net: '$630' },
  { name: 'Lightning Link #2', in_val: '$1,100', out: '$520', net: '$580' },
  { name: 'Dragon Spin #4', in_val: '$960', out: '$300', net: '$660' },
];

export const LocationPortalSection = () => (
  <section aria-label="Location Portal and Employee Management" className="py-20 md:py-32 bg-gradient-to-b from-neutral-900 to-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeInUp} className="text-amber-400 font-semibold mb-4">LOCATION PORTAL</motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6 text-white">Empower Your Location Partners</motion.h2>
          <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-8">
            Give each location their own dedicated portal to manage daily operations.
            Location owners can track bookkeeping, manage employees, submit service calls,
            and view reports — all from their phone or computer.
          </motion.p>
          <motion.ul variants={stagger} className="space-y-4">
            {PORTAL_FEATURES.map((item, i) => (
              <motion.li key={`loc-feature-${i}`} variants={fadeInUp} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-300">{item}</span>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative overflow-hidden rounded-xl">
            <div className="absolute -inset-4 bg-amber-500/10 rounded-2xl blur-2xl pointer-events-none" />
            <div className="relative bg-neutral-950 rounded-xl border border-amber-500/20 shadow-2xl overflow-hidden text-sm" data-testid="portal-mockup">
              <div className="bg-neutral-900 border-b border-white/5 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-amber-400" />
                  </div>
                  <span className="font-semibold text-white text-sm">Ace Amusements</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Shift A</span>
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-teal-400">JD</span>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-neutral-900 rounded-lg p-3 border border-white/5">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Money In</div>
                    <div className="text-lg font-bold text-green-400">$4,280</div>
                  </div>
                  <div className="bg-neutral-900 rounded-lg p-3 border border-white/5">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Money Out</div>
                    <div className="text-lg font-bold text-red-400">$1,650</div>
                  </div>
                  <div className="bg-neutral-900 rounded-lg p-3 border border-white/5">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Net Profit</div>
                    <div className="text-lg font-bold text-amber-400">$2,630</div>
                  </div>
                </div>
                <div className="bg-neutral-900 rounded-lg border border-white/5 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
                    <span className="text-xs font-semibold text-white">Today's Machine Readings</span>
                    <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Mar 12, 2026</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-white/5">
                        <th className="text-left px-4 py-2 font-medium">Machine</th>
                        <th className="text-right px-4 py-2 font-medium">In</th>
                        <th className="text-right px-4 py-2 font-medium">Out</th>
                        <th className="text-right px-4 py-2 font-medium">Net</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      {MACHINE_ROWS.map((row) => (
                        <tr key={row.name} className="border-b border-white/5 last:border-0">
                          <td className="px-4 py-2 text-white font-medium">{row.name}</td>
                          <td className="text-right px-4 py-2 text-green-400">{row.in_val}</td>
                          <td className="text-right px-4 py-2 text-red-400">{row.out}</td>
                          <td className="text-right px-4 py-2 text-amber-400 font-semibold">{row.net}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-teal-500/10 border border-teal-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
                    <PenLine className="w-3.5 h-3.5 text-teal-400" />
                    <span className="text-xs text-teal-400 font-medium">Sign Collection</span>
                  </div>
                  <div className="flex-1 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs text-amber-400 font-medium">View Receipt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
